import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { VEHICLE_ART, VEHICLE_FLIGHT, VEHICLE_PAINT } from "./SkyVehicles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_FLYING = 4;
const LAUNCH_EVERY_MS = 7000;
const FLIGHT_SECONDS = 26;
const LANES = ["8%", "30%", "55%", "78%"];
export const BALLOON_HEX = {
  red: "#ef4444", orange: "#f97316", amber: "#f59e0b", green: "#22c55e", sky: "#0ea5e9",
  blue: "#3b82f6", violet: "#8b5cf6", fuchsia: "#d946ef", pink: "#ec4899", gold: "#fbbf24",
};
const SIZE_SCALE = { standard: 1, big: 1.25, jumbo: 1.55 };

// 👑 Logo Balloon — the merchant's brand IS the balloon (premium Shape Shop buy).
const LogoBalloon = ({ url, w = 56 }) => (
  <span className="relative block" data-testid="logo-balloon" style={{ width: w, height: Math.round(w * 1.3) }}>
    <img src={url} alt="" className="absolute top-0 left-0 rounded-full object-cover bg-white shadow-lg"
      style={{ width: w, height: w, border: "2.5px solid #fff", boxShadow: "0 0 16px rgba(251,191,36,0.45)" }}
      onError={(e) => { e.currentTarget.style.display = "none"; }} />
    <span className="absolute rounded-full bg-white/50 pointer-events-none"
      style={{ width: Math.round(w * 0.22), height: Math.round(w * 0.13), top: Math.round(w * 0.12), left: Math.round(w * 0.14), transform: "rotate(-30deg)" }} />
    <svg viewBox="0 0 10 22" className="absolute left-1/2 -translate-x-1/2" style={{ top: w - 2, width: 10, height: Math.round(w * 0.32) }}>
      <polygon points="5,0 2,4 8,4" fill="#e2e8f0" />
      <path d="M5 4 Q3 10 5 16 Q7 20 5 22" stroke="#cbd5e1" strokeWidth="1.2" fill="none" />
    </svg>
  </span>
);


export const BalloonSvg = ({ color = "red", w = 56 }) => {
  const hex = BALLOON_HEX[color] || BALLOON_HEX.red;
  return (
    <svg width={w} height={w * 1.25} viewBox="0 0 40 50" style={{ filter: `drop-shadow(0 0 12px ${hex}66)` }}>
      <ellipse cx="20" cy="17" rx="15.5" ry="17" fill={hex} />
      <ellipse cx="14" cy="11" rx="4.5" ry="6.5" fill="white" opacity="0.3" />
      <path d="M20 34 L16.5 39.5 L23.5 39.5 Z" fill={hex} />
      <path d="M20 39.5 Q22 44 20 50" stroke="#94a3b8" strokeWidth="1.2" fill="none" />
    </svg>
  );
};

// 🎈 POGO BALLOON ADS — sponsored balloons + poppable WILD balloons drifting up the map.
export const SponsoredBalloons = () => {
  const [ads, setAds] = useState([]);
  const [flying, setFlying] = useState([]);
  const [open, setOpen] = useState(null); // ad tapped
  const [bucksToast, setBucksToast] = useState(null);
  const idxRef = useRef(0);
  const seqRef = useRef(0);
  const wildRef = useRef(0);
  const flyingRef = useRef([]); // mirror of flying — timers/loops read this, never stale state
  const removalT = useRef(new Map()); // balloon key -> removal timeout
  const toastT = useRef(null);

  const syncFlying = (next) => { flyingRef.current = next; setFlying(next); };

  const removeBalloon = (key) => {
    const t = removalT.current.get(key);
    if (t) { clearTimeout(t); removalT.current.delete(key); }
    syncFlying(flyingRef.current.filter((x) => x.key !== key));
  };

  useEffect(() => () => {
    removalT.current.forEach((t) => clearTimeout(t));
    removalT.current.clear();
    clearTimeout(toastT.current);
  }, []);

  useEffect(() => {
    axios.get(`${API}/balloon-ads/active`).then((r) => setAds(r.data.ads || [])).catch(() => {});
  }, []);

  // 🏆 GOLD SQUAD BALLOONS — rare gold balloons fly only for POG'ALONG squad members. (?gold / ?goldlock to preview)
  const goldRef = useRef(false);
  const goldPreviewRef = useRef(null); // captured at mount — the SPA strips query params after load
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    if (qs.has("goldlock")) { goldPreviewRef.current = "goldlock"; return; }
    if (qs.has("gold")) { goldPreviewRef.current = "gold"; goldRef.current = true; return; }
    axios.get(`${API}/pogalong/gold-status`, { withCredentials: true })
      .then((r) => { goldRef.current = !!r.data.in_squad; }).catch(() => {});
  }, []);

  // 📊 Batch-report balloon sightings (impressions) for merchant analytics.
  const impRef = useRef([]);
  useEffect(() => {
    const t = setInterval(() => {
      const ids = impRef.current.splice(0);
      if (ids.length) axios.post(`${API}/balloon-ads/impressions`, { ad_ids: ids }).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const launch = () => {
      if (flyingRef.current.length >= MAX_FLYING) return;
      const seq = seqRef.current++;
      const base = { key: `bal-${seq}`, lane: LANES[seq % LANES.length], sway: 18 + (seq % 3) * 10 };
      let inst;
      const wantWild = !ads.length || seq % 3 === 2;
      if (wantWild) {
        wildRef.current += 1;
        const wildColors = Object.keys(BALLOON_HEX);
        const goldEvery = goldPreviewRef.current === "gold" ? 2 : 8;
        if (goldRef.current && wildRef.current % goldEvery === 1) {
          inst = { ...base, kind: "gold" };
        } else if (!goldRef.current && (wildRef.current % 10 === 5 || goldPreviewRef.current === "goldlock")) {
          inst = { ...base, kind: "goldlock" };
        } else if (wildRef.current % 5 === 0) {
          const fleet = [null, "ufo", "blimp", "rocket", "hot_air", "drone"];
          const demo = fleet[Math.floor(wildRef.current / 5) % fleet.length];
          inst = { ...base, kind: "adhere", demo, color: wildColors[(seq * 7) % wildColors.length] };
          if (demo) { inst.cfg = VEHICLE_FLIGHT[demo]; inst.dur = inst.cfg.dur; }
        } else {
          inst = { ...base, kind: "wild", color: wildColors[(seq * 7 + wildRef.current) % wildColors.length] };
        }
      } else {
        const ad = ads[idxRef.current % ads.length];
        idxRef.current += 1;
        impRef.current.push(ad.id);
        inst = { ...base, kind: "ad", ad };
      }
      const cfg = inst.cfg || (inst.kind === "ad" && !inst.ad.holiday_emoji ? VEHICLE_FLIGHT[inst.ad.vehicle] : null);
      inst.dur = cfg?.dur || FLIGHT_SECONDS;
      inst.cfg = cfg;
      inst.bornAt = Date.now();
      syncFlying([...flyingRef.current, inst]);
      removalT.current.set(inst.key, setTimeout(() => removeBalloon(inst.key), inst.dur * 1000 + 1500));
    };
    launch();
    const t = setInterval(launch, LAUNCH_EVERY_MS);
    // Failsafe sweep: nothing outlives its flight — stuck/leaked balloons can
    // never clog MAX_FLYING and stop new launches.
    const sweep = setInterval(() => {
      const now = Date.now();
      flyingRef.current
        .filter((x) => now - x.bornAt > (x.dur || FLIGHT_SECONDS) * 1000 + 4000)
        .forEach((x) => removeBalloon(x.key));
    }, 10000);
    return () => { clearInterval(t); clearInterval(sweep); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  const toast = (msg) => {
    setBucksToast(msg);
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setBucksToast(null), 3000);
  };

  // 💰 VAULT FEVER — jackpot over 300? Balloons rain gold sparkles. (?fever to preview)
  const [fever, setFever] = useState(false);
  useEffect(() => {
    const announce = () => {
      if (sessionStorage.getItem("airpog_fever_toast")) return;
      sessionStorage.setItem("airpog_fever_toast", "1");
      toast("💰 VAULT FEVER — the jackpot's over 300! Balloons are raining gold ✨", { duration: 6000 });
    };
    if (window.location.search.includes("fever")) { setFever(true); announce(); return; }
    axios.get(`${API}/pogwalk/leaderboard`).then((r) => {
      const on = (r.data.vault?.amount || 0) >= 300;
      setFever(on);
      if (on) announce();
    }).catch(() => {});
  }, []);

  const tap = async (ad) => {
    setOpen(ad);
    try {
      const r = await axios.post(`${API}/balloon-ads/${ad.id}/tap`, {}, { withCredentials: true });
      if (r.data.hunt_reward) {
        toast(`🎯 SHAPE HUNT COMPLETE! +${r.data.hunt_reward} POG BUCKS! 🏆`);
      } else if (r.data.bucks_awarded > 0) {
        const hunt = r.data.hunt_progress ? ` · Shape Hunt ${r.data.hunt_progress}/${r.data.hunt_target} 🎯` : "";
        toast(`+${r.data.bucks_awarded} POG BUCK${r.data.bucks_awarded > 1 ? "S" : ""} 🥞 for the tap!${hunt}`);
      }
    } catch { /* still show the card */ }
  };

  const popWild = async (inst) => {
    removeBalloon(inst.key);
    confetti({ particleCount: 28, spread: 55, scalar: 0.7,
      origin: { x: parseFloat(inst.lane) / 100 + 0.04, y: 0.55 } });
    try {
      const r = await axios.post(`${API}/wild-balloons/pop`, {}, { withCredentials: true });
      toast(r.data.message);
    } catch { toast("POP! 💥"); }
  };

  const popGold = async (inst) => {
    removeBalloon(inst.key);
    confetti({ particleCount: 70, spread: 75, scalar: 0.9,
      colors: ["#fbbf24", "#fde68a", "#f59e0b", "#ffffff"],
      origin: { x: parseFloat(inst.lane) / 100 + 0.04, y: 0.55 } });
    // 🔔 Golden chime (arpeggio) + screen shake — a gold pop should feel HUGE.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      [880, 1174.66, 1567.98].forEach((freq, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.09);
        g.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + i * 0.09 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.09 + 0.5);
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + i * 0.09); o.stop(ctx.currentTime + i * 0.09 + 0.55);
      });
    } catch { /* audio blocked pre-gesture */ }
    document.body.classList.add("pog-gold-shake");
    setTimeout(() => document.body.classList.remove("pog-gold-shake"), 500);
    try {
      const r = await axios.post(`${API}/pogalong/gold-pop`, {}, { withCredentials: true });
      toast(r.data.message);
    } catch (e) { toast(e.response?.data?.detail || "POP! 💥 Gold dust everywhere! ✨"); }
  };

  return (
    <>
      <AnimatePresence>
        {flying.map((f) => (
          <motion.button
            key={f.key}
            data-testid={f.kind === "ad" ? `sponsored-balloon-${f.ad.id}` : f.kind === "adhere" ? "adhere-balloon" : f.kind === "gold" ? "gold-squad-balloon" : f.kind === "goldlock" ? "gold-locked-balloon" : "wild-balloon"}
            onClick={() => (f.kind === "ad" ? tap(f.ad) : f.kind === "adhere" ? setOpen({ adhere: true, demo: f.demo }) : f.kind === "gold" ? popGold(f) : f.kind === "goldlock" ? setOpen({ goldlock: true }) : popWild(f))}
            initial={f.cfg?.horizontal ? { x: "-20vw", opacity: 0 } : { y: "20vh", opacity: 0 }}
            animate={f.cfg?.horizontal
              ? { x: "115vw", opacity: 1, y: [0, 8, -8, 4, 0] }
              : { y: "-125vh", opacity: 1, x: [0, (f.cfg?.sway ?? f.sway), -(f.cfg?.sway ?? f.sway), (f.cfg?.sway ?? f.sway) * 0.5, 0], rotate: [0, f.cfg?.rotate ?? 2, -(f.cfg?.rotate ?? 2), (f.cfg?.rotate ?? 2) * 0.5, 0] }}
            exit={{ opacity: 0 }}
            transition={{
              y: { duration: f.dur || FLIGHT_SECONDS, ease: f.cfg?.horizontal ? "easeInOut" : "linear" },
              opacity: { duration: 0.6 },
              x: { duration: f.dur || FLIGHT_SECONDS, ease: f.cfg?.horizontal ? "linear" : "easeInOut" },
              rotate: { duration: f.dur || FLIGHT_SECONDS, ease: "easeInOut" },
            }}
            className={`absolute z-[1835] flex flex-col items-center pointer-events-auto select-none cursor-pointer ${f.cfg?.horizontal ? "" : "bottom-0"}`}
            style={f.cfg?.horizontal ? { top: "14%", left: 0 } : { left: f.lane }}
          >
            {fever && (
              <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none" data-testid="vault-fever-sparkle">
                <span className="text-sm animate-bounce" style={{ filter: "drop-shadow(0 0 6px rgba(251,191,36,0.9))" }}>✨</span>
                <span className="text-[10px] animate-pulse" style={{ filter: "drop-shadow(0 0 6px rgba(251,191,36,0.9))" }}>✨</span>
              </span>
            )}
            {f.kind === "ad" ? (
              <>
                {f.ad.holiday_emoji ? (
                  <span className="block leading-none" style={{ fontSize: Math.round(52 * (SIZE_SCALE[f.ad.size] || 1)), filter: "drop-shadow(0 0 14px rgba(251,191,36,0.55))" }}>
                    {f.ad.holiday_emoji}
                  </span>
                ) : f.cfg && VEHICLE_ART[f.ad.vehicle] ? (
                  (() => {
                    const Art = VEHICLE_ART[f.ad.vehicle];
                    const el = <Art w={Math.round(f.cfg.w * (SIZE_SCALE[f.ad.size] || 1))} tint={VEHICLE_PAINT[f.ad.vehicle_color] || null} />;
                    if (f.ad.vehicle === "plane" && f.ad.headline) {
                      return (
                        <span className="flex items-center">
                          <span data-testid={`plane-banner-${f.ad.id}`} className="px-2 py-0.5 rounded-sm bg-white/95 border border-slate-300 text-slate-900 text-[10px] font-black whitespace-nowrap shadow-md max-w-[160px] truncate">
                            {f.ad.headline}
                          </span>
                          <span className="inline-block w-3 h-[2px] bg-slate-300" />
                          {el}
                        </span>
                      );
                    }
                    return el;
                  })()
                ) : f.ad.logo_url ? (
                  <LogoBalloon url={`${BACKEND}${f.ad.logo_url}`} w={Math.round(56 * (SIZE_SCALE[f.ad.size] || 1))} />
                ) : f.ad.shape_emoji ? (
                  <span data-testid={`shape-balloon-${f.ad.id}`} className="block leading-none" style={{ fontSize: Math.round(52 * (SIZE_SCALE[f.ad.size] || 1)), filter: "drop-shadow(0 0 14px rgba(232,121,249,0.6))" }}>
                    {f.ad.shape_emoji}
                  </span>
                ) : (
                  <BalloonSvg color={f.ad.color} w={Math.round(54 * (SIZE_SCALE[f.ad.size] || 1))} />
                )}
                <span className={`flex flex-col items-center gap-0.5 bg-white rounded-xl px-1.5 py-1 shadow-xl border -mt-1.5 ${f.ad.super ? "border-fuchsia-400 ring-2 ring-fuchsia-400/70 shadow-fuchsia-500/50" : "border-slate-300"}`}
                  style={{ maxWidth: `${Math.round(7 * (SIZE_SCALE[f.ad.size] || 1))}rem` }}>
                  {f.ad.super && (
                    <span data-testid={`super-balloon-${f.ad.id}`} className="absolute -left-6 top-1/3 text-[16px] animate-pulse" style={{ filter: "drop-shadow(0 0 10px rgba(232,121,249,0.95))" }}>🚀</span>
                  )}                  {f.ad.vehicle && f.ad.logo_url && (
                    <img src={`${BACKEND}${f.ad.logo_url}`} alt="" data-testid={`vehicle-logo-${f.ad.id}`} style={{ width: 22, height: 22 }} className="object-cover rounded-full border border-slate-200" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                  {f.ad.image_url && <img src={`${BACKEND}${f.ad.image_url}`} alt="" style={{ width: Math.round(56 * (SIZE_SCALE[f.ad.size] || 1)), height: Math.round(56 * (SIZE_SCALE[f.ad.size] || 1)) }} className="object-contain rounded-lg" />}
                  {f.ad.headline && <span className={`font-black text-slate-800 text-center leading-tight line-clamp-2 ${f.ad.size === "jumbo" ? "text-[11px]" : f.ad.size === "big" ? "text-[10px]" : "text-[9px]"}`}>{f.ad.headline}</span>}
                  {!f.ad.image_url && !f.ad.headline && <span className="text-[9px] font-black text-slate-800">{f.ad.business_name}</span>}
                </span>
              </>
            ) : f.kind === "gold" || f.kind === "goldlock" ? (
              <span className="relative flex flex-col items-center">
                <span className="absolute -top-3.5 text-[14px] pointer-events-none" style={{ filter: "drop-shadow(0 0 8px rgba(251,191,36,0.95))" }}>{f.kind === "gold" ? "👑" : "🔒"}</span>
                <BalloonSvg color="gold" w={52} />
                <span className={`-mt-1.5 px-1.5 py-0.5 rounded-full bg-amber-400 text-[8px] font-black text-amber-950 shadow-lg whitespace-nowrap ring-1 ring-amber-200 ${f.kind === "gold" ? "animate-pulse" : "opacity-90"}`}>
                  {f.kind === "gold" ? "SQUAD GOLD 🏆" : "SQUAD GOLD 🔒"}
                </span>
              </span>
            ) : (
              <>
                {f.kind === "adhere" && f.demo && VEHICLE_ART[f.demo]
                  ? (() => { const Art = VEHICLE_ART[f.demo]; return <Art w={VEHICLE_FLIGHT[f.demo].w} />; })()
                  : <BalloonSvg color={f.color} w={f.kind === "adhere" ? 58 : 46} />}
                {f.kind === "adhere" && (
                  <span className="bg-white rounded-xl px-2 py-1 shadow-xl border border-slate-300 -mt-1.5 text-[9px] font-black text-slate-800 whitespace-nowrap">
                    {f.demo ? `YOUR ${f.demo === "hot_air" ? "HOT AIR BALLOON" : f.demo.toUpperCase()} HERE 👀` : "YOUR AD HERE 👀"}
                  </span>
                )}
              </>
            )}
          </motion.button>
        ))}
      </AnimatePresence>

      <AnimatePresence>
        {bucksToast && (
          <motion.div initial={{ opacity: 0, y: 16, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, x: "-50%" }}
            data-testid="balloon-bucks-toast"
            className="absolute bottom-40 left-1/2 z-[1900] glass-strong rounded-full px-4 py-2 text-sm font-heading font-bold text-amber-200 ring-1 ring-amber-300/50">
            {bucksToast}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center p-5" onClick={() => setOpen(null)}>
            <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, opacity: 0 }}
              data-testid="balloon-ad-card" onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-3xl p-6 text-center max-w-xs w-full border border-fuchsia-400/30">
              <button data-testid="balloon-ad-close" onClick={() => setOpen(null)} className="absolute top-3 right-3 p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              {open.goldlock ? (
                <>
                  <span className="text-4xl">👑</span>
                  <p className="font-heading font-black text-lg text-white mt-2">GOLD SQUAD BALLOONS</p>
                  <p className="text-sm text-slate-300 mt-1">Squad up in POG'ALONG and rare GOLD balloons start flying for you — pop one and your WHOLE squad splits the POG BUCKS. Plus… POG TAG a squadmate! 🎯</p>
                  <a data-testid="goldlock-cta" href="/pogalong"
                    className="mt-4 w-full h-12 rounded-2xl bg-gradient-to-r from-amber-400 to-yellow-500 font-heading font-black text-slate-900 flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
                    Squad up in POG'ALONG 👥
                  </a>
                  <p className="text-[10px] text-slate-500 mt-2">GOLD flies for squads only 🏆</p>
                </>
              ) : open.adhere ? (
                <>
                  <span className="text-4xl">{open.demo === "ufo" ? "🛸" : open.demo === "rocket" ? "🚀" : open.demo === "drone" ? "🚁" : "🎈"}</span>
                  <p className="font-heading font-black text-lg text-white mt-2">{open.demo ? `This could be YOUR ${open.demo === "hot_air" ? "hot air balloon" : open.demo}!` : "This could be YOUR balloon!"}</p>
                  <p className="text-sm text-slate-300 mt-1">Put your logo or deal on {open.demo ? "a premium Sky Fleet ride" : "a balloon"} drifting over every nearby driver's map. Claim your business & go Pro to launch one.</p>
                  <a data-testid="adhere-claim-link" href="/business"
                    className="mt-4 w-full h-12 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-amber-500 font-heading font-black text-white flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
                    Get YOUR balloon up there 🎈
                  </a>
                  <a data-testid="adhere-fleet-link" href="/fleet" className="block mt-2 text-xs font-bold text-sky-300 hover:text-sky-200">Browse the full Sky Fleet →</a>
                  <p className="text-[10px] text-slate-500 mt-2">AIR.POG Balloon Ads</p>
                </>
              ) : (
                <>
                  {open.logo_url ? (
                    <img src={`${BACKEND}${open.logo_url}`} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white/30 mx-auto" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <span className="text-4xl">{open.holiday_emoji || open.shape_emoji || "🎈"}</span>
                  )}
                  {open.image_url && <img src={`${BACKEND}${open.image_url}`} alt="" className="w-28 h-28 object-contain rounded-2xl mx-auto my-3 bg-white p-1.5" />}
                  <p className="font-heading font-black text-lg text-white">{open.business_name}</p>
                  {open.headline && <p className="text-sm text-amber-200 font-bold mt-1">{open.headline}</p>}
                  <a data-testid="balloon-ad-visit" href={open.link_url} target="_blank" rel="noopener noreferrer"
                    className="mt-4 w-full h-12 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-amber-500 font-heading font-black text-white flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
                    Check it out <ExternalLink className="w-4 h-4" />
                  </a>
                  <p className="text-[10px] text-slate-500 mt-2">Sponsored POGO Balloon 🎈</p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
